/**
 * AIConsentContext.tsx
 * Global AI Coach consent management
 * Shows consent modal once at app level, available everywhere
 *
 * Migration Week 3: Privacy Foundation
 */

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { AIConsentModal } from '../components/ai/AIConsentModal';

interface AIConsentContextType {
  // Consent status
  hasConsented: boolean | null; // null = not yet determined
  isLoading: boolean;

  // Actions
  checkConsent: () => Promise<void>;
  grantConsent: () => Promise<void>;
  revokeConsent: () => Promise<void>;
}

const AIConsentContext = createContext<AIConsentContextType | undefined>(undefined);

export const useAIConsent = () => {
  const context = useContext(AIConsentContext);
  if (!context) {
    throw new Error('useAIConsent must be used within AIConsentProvider');
  }
  return context;
};

interface AIConsentProviderProps {
  children: React.ReactNode;
}

export const AIConsentProvider: React.FC<AIConsentProviderProps> = ({ children }) => {
  const [hasConsented, setHasConsented] = useState<boolean | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showConsentModal, setShowConsentModal] = useState(false);
  const [checkedOnce, setCheckedOnce] = useState(false);

  /**
   * Check if user has consented to AI Coach
   */
  const checkConsent = useCallback(async () => {
    try {
      setIsLoading(true);

      const { data: { user } } = await supabase.auth.getUser();

      if (!user) {
        setHasConsented(false);
        setIsLoading(false);
        return;
      }

      const { data, error } = await supabase
        .from('profiles')
        .select('ai_coach_enabled, ai_coach_consent_at')
        .eq('id', user.id)
        .maybeSingle();

      if (error) {
        console.error('[AIConsentContext] Error checking AI consent:', error);
        // Don't block - just default to false
        setHasConsented(false);
        setIsLoading(false);
        return;
      } else if (!data) {
        // Profile doesn't exist yet - create it
        const { error: insertError } = await supabase
          .from('profiles')
          .insert({
            id: user.id,
            email: user.email,
            ai_coach_enabled: false,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString()
          });

        if (insertError) {
          console.error('Error creating profile:', insertError);
        }
        setHasConsented(false);
      } else {
        const consented = data.ai_coach_enabled === true && data.ai_coach_consent_at !== null;
        setHasConsented(consented);

        // UX IMPROVEMENT: Don't show modal immediately
        // Let contextual triggers handle showing consent at value moments
        // This improves acceptance rate and reduces consent fatigue

        // Set checkedOnce to prevent multiple checks
        if (!checkedOnce) {
          setCheckedOnce(true);
        }

        // Modal will be triggered contextually by:
        // - ContextualAIConsent component (when user is stuck)
        // - AIFeaturePlaceholder (when user tries to use AI feature)
        // - Manual trigger from Privacy Settings
      }
    } catch (error) {
      console.error('Error in checkConsent:', error);
      setHasConsented(false);
    } finally {
      setIsLoading(false);
    }
  }, [checkedOnce]);

  /**
   * Grant AI Coach consent
   */
  const grantConsent = useCallback(async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();

      if (!user) {
        throw new Error('No authenticated user');
      }

      const { error } = await supabase
        .from('profiles')
        .update({
          ai_coach_enabled: true,
          ai_coach_consent_at: new Date().toISOString(),
          ai_coach_consent_version: 'v1.0'
        })
        .eq('id', user.id);

      if (error) {
        console.error('Error granting consent:', error);
        throw error;
      }

      setHasConsented(true);
      setShowConsentModal(false);
    } catch (error) {
      console.error('Error in grantConsent:', error);
      throw error;
    }
  }, []);

  /**
   * Revoke AI Coach consent
   */
  const revokeConsent = useCallback(async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();

      if (!user) {
        throw new Error('No authenticated user');
      }

      const { error } = await supabase
        .from('profiles')
        .update({
          ai_coach_enabled: false,
          ai_coach_consent_at: null,
          ai_coach_consent_version: null
        })
        .eq('id', user.id);

      if (error) {
        console.error('Error revoking consent:', error);
        throw error;
      }

      setHasConsented(false);
    } catch (error) {
      console.error('Error in revokeConsent:', error);
      throw error;
    }
  }, []);

  /**
   * Dismiss consent modal (user chose to skip)
   */
  const dismissModal = useCallback(() => {
    setShowConsentModal(false);
  }, []);

  // Check consent on mount
  useEffect(() => {
    checkConsent();
  }, [checkConsent]);

  // Safety timeout: If loading takes more than 10 seconds, force it to complete
  // Increased from 3s to allow OAuth callback to complete session initialization
  useEffect(() => {
    if (isLoading) {
      const timeoutId = setTimeout(() => {
        console.warn('[AIConsentContext] Loading timeout - forcing completion');
        setIsLoading(false);
        setHasConsented(false);
      }, 10000);

      return () => clearTimeout(timeoutId);
    }
  }, [isLoading]);

  const value: AIConsentContextType = {
    hasConsented,
    isLoading,
    checkConsent,
    grantConsent,
    revokeConsent
  };

  return (
    <AIConsentContext.Provider value={value}>
      {children}

      {/* Global AI Consent Modal - Shows ONCE for entire app */}
      <AIConsentModal
        isOpen={showConsentModal}
        onConsent={(consented) => {
          if (consented) {
            grantConsent();
          } else {
            dismissModal();
          }
        }}
        onClose={dismissModal}
      />
    </AIConsentContext.Provider>
  );
};

export default AIConsentProvider;
